import { Button, Typography } from "@mui/material"
import { postServiceCenterContact, SingleServiceCenterDto } from "api"
import { trackPopUpClicks, trackSuccessLead } from "common/analytics"
import FormikForm from "common/bit/form/FormikForm"
import PrivacyPolicyCaption from "common/PrivacyPolicyCaption"
import { format } from "date-fns"
import FormInput from "reusable/formsComp/FormInput"
import FormSelect from "reusable/formsComp/FormSelect"
import styled from "styled-components"
import DateTimeInput from "../../../../reusable/formsComp/DateTimeInput"
import { FormStatus } from "../popups/ContentUsPopup"
import {
  initialFormState,
  PROBLEM_DESCRIPTION_MAX_LENGTH,
  schema,
  treatmentTypes,
} from "./TreatmentSchema"

const DATE_FORMAT = "dd/MM/yyyy"
const TIME_FORMAT = "HH:mm"

const StyledFormDiv = styled.div<{ $hide: string }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 2px;
  width: 100%;
  .MuiSelect-nativeInput {
    opacity: ${(p) => (p.$hide ? 0 : 1)};
    border: none;
    height: 100%;
    ::placeholder {
      color: rgba(16, 16, 16, 1);
      font-size: 18px;
      padding-right: 10px;
      font-family: MazdaType, OENaPro, sans-serif;
    }
  }
  label {
    font-family: MazdaType, OENaPro, sans-serif;
  }
  ${(p) => p.theme.breakpoints.down("sm")} {
    padding-left: 20px;
    padding-right: 20px;
  }
`

const StyledCol = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const StyledRowCol = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  margin-top: auto;
  gap: 2px;
  ${(p) => p.theme.breakpoints.up("md")} {
    flex-direction: row;
    column-gap: 16px;
  }
`
const StyledWrap = styled.div`
  width: 100%;
  ${(p) => p.theme.breakpoints.up("md")} {
    height: 81.6px;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
`
const StyledButton = styled(Button)`
  margin-top: 10px;
  ${(p) => p.theme.breakpoints.up("md")} {
    margin-top: 4px;
    height: 41px;
  }
`
const StyledRecallComment = styled(Typography).attrs({ variant: "body2" })`
  /* margin-left: auto; */
`
const StyledPhoneInput = styled(FormInput)`
  .MuiFormHelperText-root {
    text-align: right;
  }
`

type Props = {
  centerData: SingleServiceCenterDto
  setFormStatus: any
}
const TreatmentForm = ({ centerData, setFormStatus }: Props) => {
  // Helper: parse treatmentSchedule string to { [weekday]: [{min, max}] }
  // Parse treatmentSchedule to { [weekday]: [{min, max}] }
  const parseTreatmentSchedule = (scheduleData) => {
    if (!scheduleData) return null
    if (typeof scheduleData === "object" && !Array.isArray(scheduleData)) {
      const result = {}
      Object.entries(scheduleData).forEach(([range, daysArr]) => {
        const [min, max] = range.split("-").map((t) => t.trim())
        const days = Array.isArray(daysArr)
          ? daysArr
          : String(daysArr)
              .split(",")
              .map((d) => d.replace(/['"\s]/g, ""))
              .filter(Boolean)
        days.forEach((day) => {
          if (!result[day]) result[day] = []
          result[day].push({ min, max })
        })
      })
      return result
    }
    // Otherwise, parse as string
    const regex = /(\d{2}:\d{2}-\d{2}:\d{2}):[^[]*\[([^\]]+)\]/g
    const result = {}
    let match
    while ((match = regex.exec(scheduleData)) !== null) {
      const [range, daysStr] = [match[1], match[2]]
      const [min, max] = range.split("-").map((t) => t.trim())
      const days = daysStr
        .split(",")
        .map((d) => d.replace(/['"\s]/g, ""))
        .filter(Boolean)
      days.forEach((day) => {
        if (!result[day]) result[day] = []
        result[day].push({ min, max })
      })
    }
    return result
  }

  // Map JS getDay() to Hebrew days
  const weekdayMap = {
    0: "א", // Sunday
    1: "ב",
    2: "ג",
    3: "ד",
    4: "ה",
    5: "ו",
    6: "ש", // Saturday (not used)
  }
  // Get allowed time slots for the selected date
  const getAllowedTimeRange = (date, treatmentType) => {
    // Non main garage logic with explicit schedule
    if (!centerData.isMainGarage) {
      if (!centerData.treatmentSchedule) {
        // No schedule provided: no time slots
        return { slots: [] }
      }
      if (date) {
        const treatmentScheduleMap = parseTreatmentSchedule(
          centerData.treatmentSchedule
        )
        // Empty or unparsable schedule -> no slots at all
        if (
          !treatmentScheduleMap ||
          Object.keys(treatmentScheduleMap).length === 0
        ) {
          return { slots: [] }
        }
        const jsDay = date.getDay()
        const hebDay = weekdayMap[jsDay]
        const slots = treatmentScheduleMap[hebDay]
        if (slots && slots.length > 0) {
          return { slots }
        }
        // No slots for this specific day
        return null
      }
      // Date not chosen yet
      return { slots: [] }
    }
    // Main garage logic uses treatmentType definition only (no default hours)
    if (centerData.isMainGarage && treatmentType) {
      const type = treatmentTypes.find((t) => t.id === treatmentType)
      if (type && type.minMaxTime) {
        return {
          slots: [
            {
              min: `${type.minMaxTime.minTime}:00`,
              max: `${type.minMaxTime.maxTime}:00`,
            },
          ],
        }
      }
      // Unknown type -> no time slots
      return { slots: [] }
    }
    // No context to derive hours -> none available
    return { slots: [] }
  }
  // Get available days based on treatmentSchedule
  const getAvailableDays = () => {
    if (!centerData.isMainGarage) {
      if (!centerData.treatmentSchedule) {
        // No schedule at all -> no selectable days
        return []
      }
      const treatmentScheduleMap = parseTreatmentSchedule(
        centerData.treatmentSchedule
      )
      if (
        !treatmentScheduleMap ||
        Object.keys(treatmentScheduleMap).length === 0
      ) {
        // Empty schedule -> no selectable days
        return []
      }
      const availableDays: number[] = []
      Object.keys(treatmentScheduleMap).forEach((hebDay) => {
        const jsDay = Object.keys(weekdayMap).find(
          (key) => weekdayMap[key] === hebDay
        )
        if (jsDay) availableDays.push(parseInt(jsDay))
      })
      return availableDays
    }
    // For main garage, allow standard working days (Sunday-Thursday)
    return [0, 1, 2, 3, 4]
  }

  // Check if service center works on Friday (ו)
  // This will be handled by the availableDays logic now
  const isWorkingFriday = () => {
    if (!centerData.isMainGarage && centerData.treatmentSchedule) {
      const treatmentScheduleMap = parseTreatmentSchedule(
        centerData.treatmentSchedule
      )
      if (!treatmentScheduleMap) return false
      return Object.keys(treatmentScheduleMap).includes("ו")
    }
    return false
  }

  return (
    <FormikForm
      validationSchema={schema}
      initialValues={initialFormState}
      onSubmit={async (values) => {
        try {
          const { data } = await postServiceCenterContact({
            name: values.fullName,
            mobilePhone: values.phoneNumber,
            serviceCenterId: centerData.id,
            applicationType: "Treatment",
            rishui: values.licenseNumber,
            email: centerData.email,
            customerEmail: "",
            content: values.problemDescription ?? "",
            treatmentDate: format(values.treatmentDate, DATE_FORMAT),
            treatmentTime: format(values.treatmentDate, TIME_FORMAT),
            treatmentType: values.treatmentType || "Default",
          })
          if (data.body) {
            trackSuccessLead("קביעת ביקור", "service_center_lead_success")
            setFormStatus(FormStatus.Success)
          } else {
            setFormStatus(FormStatus.Error)
          }
        } catch (error) {
          console.error(error)
          setFormStatus(FormStatus.Error)
        }
      }}
      render={(formik) => (
        <StyledFormDiv $hide={formik.values.treatmentType}>
          <StyledRecallComment>
            לביצוע קריאה חוזרת (ריקול) יש לתאם טלפונית עם מרכז השירות.
          </StyledRecallComment>

          <StyledRowCol>
            <StyledCol>
              <FormInput
                name="fullName"
                label="שם מלא"
                required
                onFocusCapture={() =>
                  trackPopUpClicks("קביעת ביקור", "fullName")
                }
              />
              <FormSelect
                name="treatmentType"
                required
                label="סוג טיפול"
                values={treatmentTypes}
                map={(item) => ({ id: item.id as string, name: item.name })}
                onClick={() => trackPopUpClicks("קביעת ביקור", "treatmentType")}
                onChange={(event) => {
                  formik.setFieldValue("treatmentType", event.target.value)
                  // Reset date when treatment type changes as available days/times may change
                  formik.setFieldValue("treatmentDate", null)
                }}
              />
              <FormInput
                name="licenseNumber"
                label="מספר לוחית רישוי"
                required
                onFocusCapture={() =>
                  trackPopUpClicks("קביעת ביקור", "licenseNumber")
                }
              />
            </StyledCol>
            <StyledCol>
              <StyledPhoneInput
                name="phoneNumber"
                label="טלפון נייד"
                required
                type="tel"
                helperText="יש להזין טלפון זמין ליצירת קשר ואישור מועד ההגעה"
                onFocusCapture={() =>
                  trackPopUpClicks("קביעת ביקור", "phoneNumber")
                }
              />

              <DateTimeInput
                name="treatmentDate"
                label="בחירת מועד הגעה"
                allowedTimeSlots={
                  getAllowedTimeRange(
                    formik.values.treatmentDate,
                    formik.values.treatmentType
                  )?.slots
                }
                availableDays={getAvailableDays()}
                disable={!formik.values.treatmentType}
                isWorkingFriday={isWorkingFriday()}
              />
              {formik.values.treatmentType === "Problem" && (
                <FormInput
                  name="problemDescription"
                  label="פירוט התקלה"
                  required
                  multiline
                  onFocusCapture={() =>
                    trackPopUpClicks("פירוט התקלה", "problemDescription")
                  }
                  maxLength={PROBLEM_DESCRIPTION_MAX_LENGTH}
                />
              )}
              <StyledWrap>
                <StyledButton
                  disabled={formik.isSubmitting}
                  fullWidth
                  variant="text"
                  type={"submit"}
                >
                  שליחה
                </StyledButton>
              </StyledWrap>
            </StyledCol>
          </StyledRowCol>
          <PrivacyPolicyCaption />
        </StyledFormDiv>
      )}
    />
  )
}

export default TreatmentForm
